import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { Payment } from "@mercadopago/sdk-react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Waves, ArrowLeft, CheckCircle2, Copy, Loader2, QrCode, FileText, ArrowRight, Mail,
} from "lucide-react";
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ALL_PLANS } from "@/data/plans";
import { useT } from "@/i18n";

class BrickBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err) { console.log("BRICK_BOUNDARY_ERROR:", err?.message || String(err)); }
  render() {
    if (this.state.err) {
      return <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-700">
        Não foi possível carregar o formulário de pagamento. Recarregue a página.
      </div>;
    }
    return this.props.children;
  }
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Checkout() {
  const { planId } = useParams();
  const [searchParams] = useSearchParams();
  const renewCode = searchParams.get("renew") || "";
  const navigate = useNavigate();
  const t = useT();
  const plan = ALL_PLANS.find((p) => p.id === planId);

  const [email, setEmail] = useState("");
  const [started, setStarted] = useState(false);
  const [pix, setPix] = useState(null);
  const [boleto, setBoleto] = useState(null);
  const [pendingId, setPendingId] = useState(null);
  const [approvedCode, setApprovedCode] = useState(null);
  const [pollStatus, setPollStatus] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  if (!plan) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 text-center">
        <p className="text-slate-600">{t("checkout.planNotFound")}</p>
        <Link to="/"><Button className="bg-green-600 text-white hover:bg-green-700">{t("checkout.backBtn")}</Button></Link>
      </div>
    );
  }

  const startPolling = (paymentId) => {
    setPendingId(paymentId);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${paymentId}`);
        setPollStatus(data.status);
        if (data.status === "approved" && data.code) {
          clearInterval(pollRef.current);
          setApprovedCode(data.code);
        }
      } catch (_) { /* keep polling */ }
    }, 4000);
  };

  const handleSubmit = async ({ formData }) => {
    try {
      const { data } = await axios.post(`${API}/payments/process`, {
        plan_id: plan.id, email, formData, renew: renewCode || undefined,
      });
      if (data.status === "approved" && data.code) {
        setApprovedCode(data.code);
        return;
      }
      if (data.method === "pix" && data.qr_code_base64) {
        setPix({ base64: data.qr_code_base64, code: data.qr_code });
        startPolling(data.payment_id);
        return;
      }
      if (data.boleto_url || data.ticket_url) {
        setBoleto({ url: data.boleto_url || data.ticket_url });
        startPolling(data.payment_id);
        return;
      }
      if (data.status === "rejected") {
      toast.error("Pagamento recusado. Tente outro método ou cartão.");
        return;
      }
      // Fallback: start polling by payment id
      startPolling(data.payment_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao processar pagamento");
      throw e;
    }
  };

  const copy = (txt, msg) => { navigator.clipboard.writeText(txt); toast.success(msg); };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-600 text-white">
              <Waves className="h-5 w-5" />
            </span>
            <span className="font-display text-xl font-extrabold text-green-700">WX SyncVods</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6" data-testid="checkout-root">
        <button onClick={() => navigate("/")} data-testid="checkout-back"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-green-700">
          <ArrowLeft className="h-4 w-4" /> {t("checkout.back")}
        </button>

        <div className="mb-6 flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{t("checkout.buying")}</p>
            <h1 className="font-display text-xl font-bold text-slate-900">{plan.title}</h1>
            <p className="text-sm text-slate-500">{plan.period}</p>
          </div>
          <div className="font-display text-3xl font-extrabold text-green-700">{plan.price}</div>
        </div>

        {approvedCode ? (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl border border-green-300 bg-green-50 p-8 text-center" data-testid="checkout-approved">
            <CheckCircle2 className="mx-auto h-16 w-16 text-green-600" />
            <h2 className="mt-3 font-display text-2xl font-bold text-slate-900">{t("checkout.approvedTitle")}</h2>
            <p className="mt-1 flex items-center justify-center gap-1.5 text-sm text-slate-600">
              <Mail className="h-4 w-4" /> {t("checkout.sentCode")} <b>{email}</b>
            </p>
            <div className="mx-auto mt-5 max-w-md rounded-xl border border-slate-200 bg-white p-4 text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{t("checkout.yourCode")}</p>
              <div className="mt-1 flex items-center gap-2">
                <code data-testid="checkout-code" className="font-mono-code flex-1 break-all text-sm text-slate-700">{approvedCode}</code>
                <Button size="sm" variant="outline" onClick={() => copy(approvedCode, t("checkout.codeCopied"))}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Button data-testid="checkout-goto-panel" onClick={() => navigate(`/panel/${approvedCode}`)}
              className="mt-6 bg-green-600 py-6 text-base font-semibold text-white hover:bg-green-700">
              {t("checkout.gotoPanel")} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        ) : pix ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center" data-testid="checkout-pix">
            <QrCode className="mx-auto h-8 w-8 text-green-600" />
            <h2 className="mt-2 font-display text-xl font-bold text-slate-900">{t("checkout.pixTitle")}</h2>
            <p className="text-sm text-slate-500">{t("checkout.pixDesc")}</p>
            <img alt="QR Code PIX" data-testid="pix-qr" className="mx-auto mt-4 h-60 w-60 rounded-lg border border-slate-200"
              src={`data:image/png;base64,${pix.base64}`} />
            <div className="mx-auto mt-4 flex max-w-md items-center gap-2">
              <Input readOnly value={pix.code} className="font-mono-code text-xs"
                onFocus={(e) => e.target.select()} data-testid="pix-code" />
              <Button size="sm" variant="outline" onClick={() => copy(pix.code, t("checkout.pixCopied"))}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-5 flex items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("checkout.waitingPix")}
            </div>
          </div>
        ) : boleto ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center" data-testid="checkout-boleto">
            <FileText className="mx-auto h-8 w-8 text-green-600" />
            <h2 className="mt-2 font-display text-xl font-bold text-slate-900">{t("checkout.boletoTitle")}</h2>
            <p className="text-sm text-slate-500">{t("checkout.boletoDesc")}</p>
            <a href={boleto.url} target="_blank" rel="noreferrer">
              <Button className="mt-4 bg-green-600 text-white hover:bg-green-700" data-testid="boleto-open">{t("checkout.openBoleto")}</Button>
            </a>
            <div className="mt-5 flex items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("checkout.waiting")}
            </div>
          </div>
        ) : !started ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="checkout-email-step">
            <Label htmlFor="ck-email">{t("checkout.emailLabel")}</Label>
            <Input id="ck-email" type="email" data-testid="checkout-email-input" placeholder={t("checkout.emailPlaceholder")}
              value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
            <Button data-testid="checkout-continue" className="mt-4 w-full bg-green-600 py-6 text-base font-semibold text-white hover:bg-green-700"
              onClick={() => {
                if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error(t("checkout.emailInvalid")); return; }
                setStarted(true);
              }}>
              {t("checkout.continue")}
            </Button>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6" data-testid="checkout-brick">
            <p className="mb-3 text-sm text-slate-500">{t("checkout.choosePayment")}</p>
            <BrickBoundary>
              <Payment
                initialization={{ amount: plan.amount, payer: { email } }}
                customization={{
                  paymentMethods: {
                    creditCard: "all",
                    bankTransfer: ["pix"],
                    ticket: "all",
                    maxInstallments: 12,
                  },
                }}
                onSubmit={handleSubmit}
                onError={(e) => { console.log("Payment brick error", e); toast.error("Não foi possível carregar o pagamento. Recarregue a página."); }}
                onReady={() => {}}
              />
            </BrickBoundary>
          </div>
        )}

        {pendingId && !approvedCode && pollStatus && pollStatus !== "approved" && (
          <p className="mt-4 text-center text-xs text-slate-400">{t("checkout.status")}: {pollStatus}</p>
        )}
      </main>
    </div>
  );
}
